import { gql } from "lib/apolloClient";
import {
  coursePostFragment,
  parentPostFragment,
  courseStudentFragment,
  journalPostFragment,
  unitPlanFragment,
  resourceFragments,
  studentPostFragment,
  nodePostSelectionFragment,
  PostCommentFragment,
  PostTermFragment,
  postWithGoalsFragment,
  studentCourseFragment,
  staffDetailsFragment,
  plannerFragment,
  plannerElementSetFragment,
  plannerElementNodeSetFragment,
  postResolvedFieldValueFragment,
  subjectStandardFragment,
  subjectStandardFragmentPYP,
  standardsFragment,
  benchmarksFragment,
} from "./PostFragments";

import { userFragment } from "modules/CommonFragments";

export const getCoursePostFeedQuery = gql`
  query getCoursePostFeed(
    $id: ID!
    $after: String
    $filters: PostFilterInput
    $translationFilter: TranslatedLabelFilter
    $uids: [String!]
    $isDP: Boolean!
  ) {
    node(id: $id, type: COURSE) {
      id
      ... on Course {
        ...courseItem
      }
    }
  }
  ${coursePostFragment.coursePost}
`;

export const getFilteredPostCountQuery = gql`
  query getFilteredPostCount(
    $id: ID!
    $filters: PostFilterInput
    $folderIds: [ID!]
    $isShowHiddenFromStudents: Boolean
    $postCountFilters: PostCountFilterInput
  ) {
    node(id: $id, type: COURSE) {
      id
      ... on Course {
        id
        posts(filters: $filters) {
          totalCount
        }
        postCount(
          folderIds: $folderIds
          isShowHiddenFromStudents: $isShowHiddenFromStudents
          filters: $postCountFilters
        ) {
          draft
          published
          inApproval
          unTagged {
            draft
            published
            inApproval
          }
        }
      }
    }
  }
`;

export const getParentJournalFeedQuery = gql`
  query getParentJournalFeed(
    $id: ID!
    $after: String
    $filters: PostFilterInput
  ) {
    node(id: $id, type: FAMILY_MEMBER) {
      id
      ... on FamilyMember {
        ...parentItem
      }
    }
  }
  ${parentPostFragment.parentPost}
`;

export const getCourseAllStudentQuery = gql`
  query getCourseAllStudent(
    $id: ID!
    $folderIds: [ID!]
    $includeRemovedStudentIds: [ID!]
    $isShowHiddenFromStudents: Boolean
    $filters: PostCountFilterInput
    $userArchivedFilters: UserArchivedFilters
  ) {
    node(id: $id, type: COURSE) {
      id
      ... on Course {
        ...courseItem
      }
    }
  }
  ${courseStudentFragment.courseStudent}
`;

export const getPostDetailQuery = gql`
  query getPostDetail($id: ID!, $translationFilter: TranslatedLabelFilter) {
    node(id: $id, type: POST) {
      id
      ... on Post {
        ...postItem
      }
    }
  }
  ${journalPostFragment.journalPostDetail}
`;

export const getPostTranslationQuery = gql`
  query getPostTranslation(
    $id: ID!
    $translationFilter: TranslatedLabelFilter
  ) {
    node(id: $id, type: POST) {
      id
      ... on Post {
        ...translationPostItem
      }
    }
  }
  ${journalPostFragment.translationPostItem}
`;

export const getPostLikesDetailsQuery = gql`
  query getPostLikesDetails($id: ID!) {
    node(id: $id, type: POST) {
      ... on Post {
        ...postItem
      }
    }
  }
  ${journalPostFragment.postLikesDetails}
`;

export const getActivityPostLikesDetailsQuery = gql`
  query getActivityLike($id: ID!) {
    node(id: $id, type: STUDENT_ACTIVITY) {
      id
      ... on StudentActivity {
        id
        student {
          ...userItem
        }
        likes {
          totalCount
          edges {
            node {
              id
              user {
                id
                ...userItem
                ... on FamilyMember {
                  children {
                    ...userItem
                  }
                }
              }
            }
          }
        }
      }
    }
  }
  ${userFragment.basicUserDetails}
`;

export const getRoutinePostLikesDetailsQuery = gql`
  query getActivityLike($id: ID!) {
    node(id: $id, type: STUDENT_TEMPLATE_RESPONSE) {
      id
      ... on StudentTemplateResponse {
        id
        student {
          ...userItem
        }
        likes {
          totalCount
          edges {
            node {
              id
              user {
                id
                ...userItem
                ... on FamilyMember {
                  children {
                    ...userItem
                  }
                }
              }
            }
          }
        }
      }
    }
  }
  ${userFragment.basicUserDetails}
`;

export const getPostSeenDetailsQuery = gql`
  query getPostSeenDetails($id: ID!) {
    node(id: $id, type: POST) {
      ... on Post {
        ...postItem
      }
    }
  }
  ${journalPostFragment.postSeenDetails}
`;

export const getCourseIdForPostQuery = gql`
  query getCourseIdForPost($id: ID!) {
    node(id: $id, type: POST) {
      id
      ... on Post {
        course {
          id
        }
      }
    }
  }
`;

export const getCourseFilteredStudentQuery = gql`
  query getCourseFilteredStudent(
    $id: ID!
    $filters: CourseFilter
    $orderBy: ORDER_BY_ENUM
    $orderByDirection: ORDER_BY_DIRECTION
    $userArchivedFilters: UserArchivedFilters
  ) {
    node(id: $id, type: COURSE) {
      id
      ... on Course {
        ...courseItem
      }
    }
  }
  ${courseStudentFragment.courseFiltersStudent}
`;

export const getCourseUnitQuery = gql`
  query getCourseUnit(
    $id: ID!
    $filters: UnitPlansV2FeedFilters
    $uids: [String!]
  ) {
    node(id: $id, type: COURSE) {
      id
      ... on Course {
        id
        flatUnitPlans(filters: $filters) {
          edges {
            node {
              ...unitPlanItem
            }
          }
        }
      }
    }
  }
  ${unitPlanFragment.unitPlanDetailsWithoutFields}
`;

export const getCourseUnitQueryV2 = gql`
  query getCourseUnitV2(
    $id: ID!
    $filters: LearningCourseUnitPlanFilter
    $uids: [String!]
  ) {
    node(id: $id, type: LEARNING_COURSE) {
      id
      ... on LearningCourse {
        id
        unitPlans(filters: $filters) {
          edges {
            node {
              ...unitPlanItem
            }
          }
        }
      }
    }
  }
  ${unitPlanFragment.unitPlanDetailsWithoutFields}
`;

export const getResourceBucketQuery = gql`
  query getResourceBucketQuery(
    $ids: [ID!]
    $type: ENTITY_TYPE_ENUM_MULTI_NODE!
    $resourceFilters: resourceLibraryFilter
  ) {
    multiNode(ids: $ids, type: $type) {
      id
      ... on UnitPlan {
        id
        resourceLibrary(filters: $resourceFilters) {
          leCount
          assessmentCount
          edges {
            type
            node {
              ... on Assessment {
                ...assessmentItem
              }
              ... on LearningEngagement {
                ...leItem
              }
            }
          }
        }
      }
    }
  }
  ${resourceFragments.assessmentDetails}
  ${resourceFragments.leDetails}
`;

export const PlannerFieldFragment = {
  PlannerFieldDetail: gql`
    fragment PlannerFieldItem on PlannerField {
      id
      uid
      value
      resolvedMinimalTree {
        ...resolvedMinimalTreeItem
      }
    }
    ${postResolvedFieldValueFragment.resolvedMinimalTree}
  `,
};

export const getUnitPlanQuery = gql`
  query getUnitPlan($id: ID!, $uids: [String!]) {
    node(id: $id, type: UNIT_PLAN) {
      ... on UnitPlan {
        id
        templateId
        subjects {
          id
          resolvedMinimalTree {
            ... on ResolvedFieldSubjectSet {
              id
              type
              subjects {
                id
                name
                isDeleted
                isArchived
              }
            }
          }
        }
        fields(uids: $uids) {
          ...PlannerFieldItem
        }
      }
    }
  }
  ${PlannerFieldFragment.PlannerFieldDetail}
`;

export const fetchLEQuery = gql`
  query getLE($id: ID!, $uids: [String!]) {
    node(id: $id, type: LEARNING_ENGAGEMENT) {
      ... on LearningEngagement {
        id
        fields(uids: $uids) {
          ...PlannerFieldItem
        }
      }
    }
  }
  ${PlannerFieldFragment.PlannerFieldDetail}
`;

export const fetchAssessmentQuery = gql`
  query getAssessment($id: ID!, $uids: [String!]) {
    node(id: $id, type: ASSESSMENT) {
      ... on Assessment {
        id
        unitPlan {
          id
          subjects {
            id
            resolvedMinimalTree {
              ... on ResolvedFieldSubjectSet {
                id
                type
                subjects {
                  id
                  name
                  isDeleted
                  isArchived
                }
              }
            }
          }
        }
        fields(uids: $uids) {
          ...PlannerFieldItem
        }
      }
    }
  }
  ${PlannerFieldFragment.PlannerFieldDetail}
`;

export const getPostOfStudentQuery = gql`
  query getStudentPypTaggedPost(
    $studentId: ID!
    $after: String
    $filters: PostFilterInput
    $uids: [String!]
    $translationFilter: TranslatedLabelFilter
    $isDP: Boolean!
    $aclControlParameterFilter: ACLControlParameterFilter
  ) {
    node(id: $studentId, type: STUDENT) {
      id
      ... on Student {
        ...studentPostItem
      }
    }
  }
  ${studentPostFragment.studentProfilePortfolio}
`;

export const getStudentPostCountQuery = gql`
  query getStudentPypTaggedPost(
    $studentId: ID!
    $after: String
    $filters: PostFilterInput
  ) {
    node(id: $studentId, type: STUDENT) {
      id
      ... on Student {
        ...studentPostItem
      }
    }
  }
  ${studentPostFragment.studentPostCount}
`;

export const getNodePostQuery = gql`
  query getNodePost(
    $id: ID!
    $type: ENTITY_TYPE_ENUM!
    $filters: PostFilterInput
    $after: String
  ) {
    node(id: $id, type: $type) {
      id
      ... on Organization {
        ...organizationPostItem
      }
      ... on Student {
        ...studentPostItem
      }
    }
  }
  ${nodePostSelectionFragment.organizationPost}
  ${nodePostSelectionFragment.studentPost}
`;

export const getOrganizationPostQuery = gql`
  query getOrgPosts($id: ID!, $filters: PostFilterInput, $after: String) {
    node(id: $id, type: ORGANIZATION) {
      id
      ... on Organization {
        ...organizationPostItem
      }
    }
  }
  ${nodePostSelectionFragment.organizationPost}
`;

export const getCommentsOfPostQuery = gql`
  query getCommentsOfPost($id: ID!) {
    node(id: $id, type: POST) {
      id
      ... on Post {
        ...postItem
      }
    }
  }
  ${PostCommentFragment.post}
`;

export const getOrganizationAssessmentsQuery = gql`
  query getOrganizationAssessments(
    $id: ID!
    $first: Int
    $after: String
    $filters: AssessmentOrganizationFilters
    $uids: [String!]
  ) {
    node(id: $id, type: ORGANIZATION) {
      id
      ... on Organization {
        id
        assessments(first: $first, after: $after, filters: $filters) {
          totalCount
          pageInfo {
            hasNextPage
            startCursor
            endCursor
          }
          edges {
            node {
              ...assessmentItem
            }
          }
        }
      }
    }
  }
  ${resourceFragments.assessmentBasicDetails}
`;

export const getOrganizationAcademicYearsQuery = gql`
  query getOrganizationAcademicYears($id: ID!, $termsFilters: TermFilters) {
    node(id: $id, type: ORGANIZATION) {
      id
      ... on Organization {
        id
        academicYears {
          ...academicYearBasicDetailsItem
          __typename
        }
        __typename
      }
      __typename
    }
  }
  ${PostTermFragment.academicYearBasicDetailsItem}
`;

export const getPostsHavingTaggedGoalsQuery = gql`
  query getPostsHavingTaggedGoals(
    $id: ID!
    $last: Int
    $filters: PostFilterInput
    $type: ENTITY_TYPE_ENUM!
  ) {
    node(id: $id, type: $type) {
      id
      ... on Course {
        id
        posts(last: $last, filters: $filters) {
          totalCount
          edges {
            node {
              ...postItem
            }
          }
        }
      }
    }
  }
  ${postWithGoalsFragment.postWithGoals}
`;

export const getStudentCoursesQuery = gql`
  query getStudentCourses(
    $studentId: ID!
    $filters: UserCourseMapFilters
    $enabledModulesFilters: EnabledModulesFiltersInput
    $aclControlParameterFilter: ACLControlParameterFilter
    $postFilters: PostFilterInput
  ) {
    node(id: $studentId, type: STUDENT) {
      ...studentCoursesItem
    }
  }
  ${studentCourseFragment}
`;

export const getPlatformUserDetailsQuery = gql`
  query getPlatformUserDetails(
    $id: ID!
    $type: ENTITY_TYPE_ENUM!
    $entityRelationMapFilters: EntityRelationMapFilter
  ) {
    node(id: $id, type: $type) {
      id
      ... on Staff {
        ...staffItem
      }
    }
  }
  ${staffDetailsFragment}
`;

export const getMultiNodeUnitElementsQuery = gql`
  query getUnitPlan(
    $id: [ID!]
    $uids: [String!]
    $type: ENTITY_TYPE_ENUM_MULTI_NODE!
  ) {
    multiNode(ids: $id, type: $type) {
      ... on UnitPlan {
        id
        templateId
        subjects {
          id
          resolvedMinimalTree {
            ... on ResolvedFieldSubjectSet {
              id
              type
              subjects {
                id
                name
                isDeleted
                isArchived
              }
            }
          }
        }
        fields(uids: $uids) {
          ...PlannerFieldItem
        }
      }
      ... on Assessment {
        ...assessmentItem
      }
    }
  }
  ${resourceFragments.assessmentPYPElements}
  ${PlannerFieldFragment.PlannerFieldDetail}
`;

export const getStandardSetsQuery = gql`
  query getStandardSets(
    $id: ID!
    $type: ENTITY_TYPE_ENUM!
    $plannerElementNodeSetFilters: PlannerElementNodeSetFilters
    $plannerElementNodeFilters: PlannerElementNodeFilters
    $isPYPCurriculum: Boolean! = false
    $orderBy: OrderByEnum
    $orderByDirection: OrderByDirectionEnum
  ) {
    node(id: $id, type: $type) {
      ... on CurriculumProgram {
        plannerElementNodeSets(
          filters: $plannerElementNodeSetFilters
          orderBy: $orderBy
          orderByDirection: $orderByDirection
        ) {
          edges {
            node {
              id
              title
            }
          }
        }
      }
      ... on LearningCourse {
        id
        standardsWrapper(
          filters: $plannerElementNodeFilters
          orderByDirection: $orderByDirection
        ) @skip(if: $isPYPCurriculum) {
          standardsEdge {
            standardSet {
              id
              title
            }
          }
          totalCount
        }

        benchmarksWrapper(
          filters: $plannerElementNodeFilters
          orderByDirection: $orderByDirection
        ) @include(if: $isPYPCurriculum) {
          standardsEdge {
            standardSet {
              id
              title
            }
          }
          totalCount
        }
      }
    }
  }
`;

export const getCurriculumStandardSetsQuery = gql`
  query getCurriculumStandardSets(
    $id: ID!
    $type: ENTITY_TYPE_ENUM!
    $subjectIds: [ID!]
    $setId: ID!
    $orderBy: OrderByEnum
    $orderByDirection: OrderByDirectionEnum
  ) {
    node(id: $id, type: $type) {
      id
      ... on UBDCurriculumProgram {
        plannerElementNodeSets(
          orderBy: $orderBy
          orderByDirection: $orderByDirection
          filters: {
            status: [PUBLISHED, ARCHIVED, PARTIALLY_PUBLISHED]
            subjects: $subjectIds
            plannerElementIds: [$setId]
          }
        ) {
          ...subjectStandardFragment
        }
      }

      ... on IbMypCurriculumProgram {
        plannerElementNodeSets(
          orderBy: $orderBy
          orderByDirection: $orderByDirection
          filters: {
            status: [PUBLISHED, ARCHIVED, PARTIALLY_PUBLISHED]
            subjects: $subjectIds
            plannerElementIds: [$setId]
          }
        ) {
          ...subjectStandardFragment
        }
      }

      ... on IbPypCurriculumProgram {
        plannerElementNodeSets(
          orderBy: $orderBy
          orderByDirection: $orderByDirection
          filters: {
            status: [PUBLISHED, ARCHIVED, PARTIALLY_PUBLISHED]
            subjects: $subjectIds
            plannerElementIds: [$setId]
          }
        ) {
          ...subjectStandardFragmentPYP
        }
      }

      ... on IbDpCurriculumProgram {
        plannerElementNodeSets(
          orderBy: $orderBy
          orderByDirection: $orderByDirection
          filters: {
            status: [PUBLISHED, ARCHIVED, PARTIALLY_PUBLISHED]
            subjects: $subjectIds
            plannerElementIds: [$setId]
          }
        ) {
          ...subjectStandardFragment
        }
      }
    }
  }
  ${subjectStandardFragment}
  ${subjectStandardFragmentPYP}
`;

export const getCurriculumStandardsQuery = gql`
  query getCurriculumStandards(
    $id: ID!
    $filters: PlannerElementNodeFilters
    $isPYPCurriculum: Boolean! = false
  ) {
    node(id: $id, type: PLANNER_ELEMENT_NODE_SET) {
      ... on PlannerElementNodeSet {
        id
        standardSetWrapper {
          publishedStandards(filters: $filters) @skip(if: $isPYPCurriculum) {
            edges {
              node {
                ...standardsFragment
                parent
                associatedParents {
                  id
                }
              }
            }
          }

          publishedBenchmarks(filters: $filters)
            @include(if: $isPYPCurriculum) {
            edges {
              node {
                ...benchmarksFragment
                parent
              }
            }
          }
        }
      }
    }
  }
  ${standardsFragment}
  ${benchmarksFragment}
`;

export const getLearningCourseStandardsQuery = gql`
  query getLearningCourseStandards(
    $id: ID!
    $filters: PlannerElementNodeFilters
    $isPYPCurriculum: Boolean! = false
  ) {
    node(id: $id, type: LEARNING_COURSE) {
      id
      ... on LearningCourse {
        standardsWrapper(filters: $filters) @skip(if: $isPYPCurriculum) {
          standardsEdge {
            standardSet {
              id
            }
            standards {
              ...plannerElementNodeItem
            }
          }
        }

        benchmarksWrapper(filters: $filters) @include(if: $isPYPCurriculum) {
          standardsEdge {
            standardSet {
              id
            }
            standards {
              ...plannerBenchmarkItem
            }
          }
        }
      }
    }
  }
  ${plannerFragment.plannerElementNodeFragment}
  ${plannerFragment.plannerBenchmark}
`;

export const getPlannerElementSetsOfCurriculumQuery = gql`
  query getPlannerElementSetsOfCurriculum(
    $id: ID!
    $elementTypes: [PLANNER_ELEMENT_TYPE_ENUM]
    $filters: PlannerElementFilters
    $type: ENTITY_TYPE_ENUM!
  ) {
    node(id: $id, type: $type) {
      id
      ... on IbMypCurriculumProgram {
        id
        plannerElementSets(elementTypes: $elementTypes) {
          ...plannerElementSetWithNodesItem
        }
      }
      ... on UBDCurriculumProgram {
        id
        plannerElementSets(elementTypes: $elementTypes) {
          ...plannerElementSetWithNodesItem
        }
      }
      ... on IbDpCurriculumProgram {
        id
        plannerElementSets(elementTypes: $elementTypes) {
          ...plannerElementSetWithNodesItem
        }
      }
    }
  }

  ${plannerElementSetFragment.plannerElementSetWithNodes}
`;

export const getPlannerElementNodeSetIdsOfCurriculumQuery = gql`
  query getPlannerElementNodeSetIdsOfCurriculum(
    $id: ID!
    $type: ENTITY_TYPE_ENUM!
    $plannerElementNodeSetFilters: PlannerElementNodeSetFilters
  ) {
    node(id: $id, type: $type) {
      id
      ... on IbMypCurriculumProgram {
        id
        plannerElementNodeSets(filters: $plannerElementNodeSetFilters) {
          ...plannerElementNodeSetItem
        }
      }
      ... on UBDCurriculumProgram {
        id
        plannerElementNodeSets(filters: $plannerElementNodeSetFilters) {
          ...plannerElementNodeSetItem
        }
      }
      ... on IbDpCurriculumProgram {
        id
        plannerElementNodeSets(filters: $plannerElementNodeSetFilters) {
          ...plannerElementNodeSetItem
        }
      }
      ... on IbPypCurriculumProgram {
        id
        plannerElementNodeSets(filters: $plannerElementNodeSetFilters) {
          ...plannerElementNodeSetItem
        }
      }
    }
  }
  ${plannerElementNodeSetFragment.plannerElementNodeSet}
`;

export const getPlannerElementNodeSetsFromIdsQuery = gql`
  query getPlannerElementNodeSetsFromIds(
    $plannerElementNodeSetIds: [ID!]
    $plannerElementATLFilters: PlannerElementNodeFilters
    $plannerElementNonAtlFilters: PlannerElementNodeFilters
  ) {
    multiNode(ids: $plannerElementNodeSetIds, type: PLANNER_ELEMENT_NODE_SET) {
      ... on PlannerElementNodeSet {
        ...plannerElementNodeSetWithNodesItem
      }
    }
  }
  ${plannerElementNodeSetFragment.plannerElementNodeSetWithNodes}
`;
